import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { BehaviorSubject, Observable, of, Subscription, switchMap } from "rxjs";
import { AgendasWeekSelectorComponent } from "../../../agendas/components/week-selector/week-selector";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { AsyncPipe, formatDate, KeyValuePipe, NgFor, NgIf } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { AppDatePipe } from "../../../common/pipes/app-date";
import { MobileDiaryListSectionComponent } from "../mobile.diary-list-section.component";
import { FlexLayoutModule } from "@angular/flex-layout";
import { Organization } from "../../../common/models/organization";
import { User } from "../../../common/models/user";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { ActivatedRoute, Router } from "@angular/router";
import { SessionTimeSlotRow, TimeslotsProvider } from "../../../agendas/providers/timeslots-provider";
import { SessionsProvider } from "../../../sessions/providers/sessions-provider";
import { SessionItemsProvider } from "../../../sessions/providers/sessionItems-provider";
import { AgendaProvider } from "../../../agendas/providers/agenda-provider";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AgendaCommentsProvider } from "../../../agendas/providers/agenda-comments-provider";
import moment from "moment/moment";
import { Agenda } from "../../../common/models/agenda";
import { finalize, map } from "rxjs/operators";
import { AgendaComment } from "../../../common/models/agenda-comment";
import { Timeslot } from "../../../common/models/timeslot";
import { Session } from "../../../common/models/session";
import { MatDialog } from "@angular/material/dialog";
import {
  AgendaMobileSessionDisplayDialog
} from "../../../agendas/components/mobile-session-display-dialog/agenda-mobile-session-display-dialog";
import { LangsProvider } from "../../../common/providers/langs-provider";


@Component({
  selector: 'mobile-diary-list-content',
  providers: [],
  standalone: true,
  styleUrls: ['./mobile-diary-list-content.scss'],
  templateUrl: './mobile-diary-list-content.html',
  imports: [
    AgendasWeekSelectorComponent,
    MatProgressSpinnerModule,
    NgIf,
    NgFor,
    KeyValuePipe,
    TranslateModule,
    AppDatePipe,
    AsyncPipe,
    MobileDiaryListSectionComponent,
    FlexLayoutModule,
  ],
})
export class MobileDiaryListContentComponent implements OnInit, OnDestroy {
  @Input() public fromCoachView = false;
  @Input() public coachViewAgenda: Agenda | null = null;

  public pageTitle = 'Mobile.dashboard';
  public isLoading = true;
  public is401 = false;
  public is404 = false;
  public dashboardUrl: string | null = null;
  public organization: Organization = {} as Organization;
  public athleteUser: User = {} as User;
  public weekComment: string | undefined;
  public timeslots$: Observable<any> = new Observable<any>();
  public timeslots: any[] = [];

  public loadingSubject = new BehaviorSubject<boolean>(false);
  public hasDataSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();
  public hasData$ = this.hasDataSubject.asObservable();

  private subscriptions: Subscription[] = [];
  public currentDate: Date = new Date();

  public locale;

  constructor(
    public conf: ConfigProvider,
    public currentUser: CurrentUser,
    private _router: Router,
    private _route: ActivatedRoute,
    private timeslotProvider: TimeslotsProvider,
    private sessionsProvider: SessionsProvider,
    private sessionItemsProvider: SessionItemsProvider,
    private agendaProvider: AgendaProvider,
    private _utils: UtilsProvider,
    private _agendaCommentsProvider: AgendaCommentsProvider,
    private dialog: MatDialog,
    private langsProvider: LangsProvider,
  ) {
    this._route.params.subscribe((params: any) => {
      this.currentDate = params['date'] ? moment.utc(params['date']).toDate() : new Date();
    });
    this.locale = this.langsProvider.getBestLangConfig().dateLocale;
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this._route.data.subscribe((data: any) => { //{ organization: Organization; athlete: Athlete }

        this.organization = data.organization;
        this.athleteUser = data.athlete;
        if (this.fromCoachView) {
          this.dashboardUrl = data.organization.code + '/athletes/' + this.athleteUser.id;
        } else {
          this.dashboardUrl = data.organization.code + '/app/athletes/' + this.athleteUser.id;
        }

        this.populateTimeSlots();
        this.loadWeekComment();
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public async canChangeDate$(currentDate: Date, nextDate: Date): Promise<boolean> {
    if (this.fromCoachView && this.coachViewAgenda) {
      await this._router.navigate([this.organization!.code, 'athletes', this.athleteUser.id, 'calendar', this.coachViewAgenda.id, this._utils.DateToYmd(nextDate)]);
    } else {
      await this._router.navigate([this.organization!.code, 'app', 'athletes', this.athleteUser.id, 'diary', this._utils.DateToYmd(nextDate)]);
    }
    return false;
  };

  public onSessionClicked(sessionTimeslotRow: any) {
    if (this.fromCoachView && this.coachViewAgenda) {
      // open dialog
      this.dialog.open(AgendaMobileSessionDisplayDialog, {
        data: {
          organization: this.organization,
          athleteUser: this.athleteUser,
          library: this.coachViewAgenda,
          sessionTimeslotRow: sessionTimeslotRow,
        },
      }).afterClosed().subscribe((result: any) => {
        this.populateTimeSlots();
      });
    } else {
      this._router.navigate([this.organization!.code, 'app', 'athletes', this.athleteUser.id, 'diary', sessionTimeslotRow.id, sessionTimeslotRow.timeslotId]);
    }
  }

  private loadWeekComment() {
    this.agendaProvider.getAthleteAgenda(this.organization.id!, this.athleteUser.id!).pipe(
      switchMap((agenda: Agenda) => {
        const fromDate: Date = this._utils.firstDayOfWeek(this.currentDate);
        return this._agendaCommentsProvider.getCommentOf(this.organization.id!, agenda.id!, this._utils.DateToYmd(fromDate)).pipe(
          map((comment: AgendaComment) => {
            this.weekComment = comment ? comment.comment : undefined;
          }),
        );
      }),
    ).subscribe();
  }

  private populateTimeSlots() {
    this.loadingSubject.next(true);

    const groupByDay = (timeSlots: any) => timeSlots.reduce((acc: any, curr: any) => {
      if (!acc[curr.day]) {
        acc[curr.day] = [];
      }

      acc[curr.day].push(curr);

      return acc;
    }, {});
    this.timeslots$ = this.timeslotProvider.getTimeslotsByAthleteByWeek({athleteUserId: this.athleteUser.id!, week :this.currentDate}).pipe(
      map((timeslots: any[]) => {
        const sessionTimeSlotRows: SessionTimeSlotRow[] = [];

        const sessions = timeslots.map((timeslot: Timeslot) => timeslot.session);
        sessions.forEach((session: Session|undefined) => {
          if (!session) {
            return;
          }

          const timeslot = timeslots.find((ts: Timeslot) => ts.sessionId === session.id);

          const srow = this.sessionsProvider.createSessionRow(this.organization, session, undefined, true);

          const stsr: any = {
            id: session.id!,
            comment: session.comment,
            adn: srow.adn,
            name: session.name!,
            totalTime: session.totalTime!,
            timeslotId: timeslot.id,
            fromDate:  this._utils.formatDate(this._utils.ISODateToYmdHis(timeslot.fromDate), 'YYYY-MM-DD HH:mm:ss', false),
            day: this._utils.formatDate(this._utils.ISODateToYmdHis(timeslot.fromDate), 'YYYY-MM-DD', false),
            histogramData: this.sessionItemsProvider.getHistogramData(srow),
          };

          sessionTimeSlotRows.push(stsr);
        });

        sessionTimeSlotRows.sort((a: SessionTimeSlotRow, b: SessionTimeSlotRow) => this._utils.sortRow(a.fromDate, b.fromDate));

        this.hasDataSubject.next(sessionTimeSlotRows.length > 0);

        return groupByDay(sessionTimeSlotRows);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
      })
    );

    this.timeslots$.subscribe((timeslots: any[]) => {
      this.timeslots = timeslots;

      // scroll to current day sessions
      setTimeout(function() {
        const elements = document.getElementsByClassName('day-' + formatDate(new Date(), 'yyyy-M-d', 'en'));
        if (elements.length > 0) {
          elements[0].scrollIntoView();
        }
      }, 100);
    });
  }

  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 404) {
      this.is404 = true;
    } else if (error.status === 401) {
      this.is401 = true;
    }
  }
}
